import { useState } from "react";
import GlobalStyles from "../components/GlobalStyles";
import { CRIMES, WITNESS_TYPES } from "../constants";
import { callClaude } from "../api";
import {
  buildCasePrompt, buildReportPrompt, buildDocumentaryEvidencePrompt,
} from "../prompts";
import {
  generateReportHTML, generateDocHTML, openDoc,
} from "../utils";

const SETUP_CSS = `
.litis-setup {
  --bg:       #ECEAE3;
  --bg2:      #E3E0D8;
  --bg3:      #D6D2C8;
  --white:    #F9F8F5;
  --dark:     #1A170F;
  --text:     #2C2820;
  --muted:    #7A7268;
  --light:    #B0AA9E;
  --accent:   #C4572A;
  --accent2:  #A8451C;
  --green:    #70A33C;
  --blue:     #4A7FB5;
  --border:   rgba(30,26,20,0.10);
  --r: 10px;
  --sans: 'Fira Sans', system-ui, sans-serif;
  position: fixed; inset: 0;
  background: var(--bg);
  overflow-y: auto;
  font-family: var(--sans);
  color: var(--text);
  -webkit-font-smoothing: antialiased;
}
.litis-setup *, .litis-setup *::before, .litis-setup *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* Nav */
.litis-setup .setup-nav {
  background: rgba(236,234,227,0.95); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border); height: 56px;
  display: flex; align-items: center; padding: 0 24px; gap: 14px;
  position: sticky; top: 0; z-index: 10;
}
.litis-setup .setup-nav-logo { height: 26px; width: auto; display: block; background: none; border: none; cursor: pointer; padding: 0; }
.litis-setup .setup-nav-title { font-size: 14px; font-weight: 400; color: var(--muted); }
.litis-setup .setup-nav-right { margin-left: auto; display: flex; gap: 10px; align-items: center; }
.litis-setup .nav-user { display: flex; align-items: center; gap: 9px; }
.litis-setup .nav-user-img { width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--border); }
.litis-setup .nav-user-name { font-size: 13px; color: var(--muted); font-weight: 500; max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.litis-setup .btn-sm {
  font-family: var(--sans); font-size: 13px; font-weight: 500;
  padding: 7px 16px; border-radius: 8px; cursor: pointer; border: none; transition: all 0.2s;
  background: none; color: var(--muted); border: 1px solid var(--border);
}
.litis-setup .btn-sm:hover { color: var(--text); border-color: rgba(30,26,20,0.2); }

/* Body */
.litis-setup .setup-body { max-width: 760px; margin: 0 auto; padding: 48px 32px 80px; }
.litis-setup .setup-header { margin-bottom: 40px; }
.litis-setup .setup-header h1 { font-size: 32px; font-weight: 500; letter-spacing: -0.02em; color: var(--dark); margin-bottom: 6px; }
.litis-setup .setup-header p { font-size: 15px; color: var(--muted); }

.litis-setup .setup-step { margin-bottom: 34px; }
.litis-setup .step-label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--light); margin-bottom: 14px; display: flex; align-items: center; gap: 10px;
}
.litis-setup .step-label-num {
  width: 20px; height: 20px; border-radius: 50%; background: var(--bg3);
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; color: var(--muted); flex-shrink: 0;
}
.litis-setup .step-label-num.done { background: var(--green); color: white; }

/* Crime grid */
.litis-setup .crime-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.litis-setup .crime-card {
  background: var(--white); border: 1px solid var(--border); border-radius: var(--r);
  padding: 18px 20px; cursor: pointer; transition: border-color 0.2s, background 0.2s;
  text-align: left; font-family: var(--sans);
}
.litis-setup .crime-card:hover { border-color: rgba(30,26,20,0.2); }
.litis-setup .crime-card.selected { border-color: var(--green); background: rgba(112,163,60,0.05); }
.litis-setup .crime-card-coip { font-size: 11px; font-weight: 600; color: var(--light); margin-bottom: 4px; }
.litis-setup .crime-card.selected .crime-card-coip { color: var(--green); }
.litis-setup .crime-card-title { font-size: 16px; font-weight: 500; color: var(--dark); margin-bottom: 4px; }
.litis-setup .crime-card-desc { font-size: 13px; color: var(--muted); }

/* Role grid */
.litis-setup .role-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.litis-setup .role-card {
  background: var(--white); border: 1px solid var(--border); border-radius: var(--r);
  padding: 20px; cursor: pointer; transition: border-color 0.2s, background 0.2s;
  text-align: left; font-family: var(--sans);
}
.litis-setup .role-card:hover { border-color: rgba(30,26,20,0.2); }
.litis-setup .role-card.selected { border-color: var(--accent); background: rgba(196,87,42,0.05); }
.litis-setup .role-card-title { font-size: 16px; font-weight: 500; color: var(--dark); margin-bottom: 4px; }
.litis-setup .role-card.selected .role-card-title { color: var(--accent); }
.litis-setup .role-card-desc { font-size: 13px; color: var(--muted); }

.litis-setup .name-label { font-size: 12px; color: var(--muted); margin: 14px 0 8px; }
.litis-setup .name-input {
  width: 100%; font-family: var(--sans); font-size: 15px;
  background: var(--white); border: 1px solid var(--border); border-radius: var(--r);
  padding: 12px 16px; color: var(--text); transition: border-color 0.2s; outline: none;
}
.litis-setup .name-input:focus { border-color: var(--green); }
.litis-setup .name-input::placeholder { color: var(--light); }

/* Diff grid */
.litis-setup .diff-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.litis-setup .diff-card {
  background: var(--white); border: 1px solid var(--border); border-radius: var(--r);
  padding: 16px; cursor: pointer; transition: all 0.2s; text-align: center; font-family: var(--sans);
}
.litis-setup .diff-card:hover { border-color: rgba(30,26,20,0.2); }
.litis-setup .diff-card.selected { border-color: var(--green); background: rgba(112,163,60,0.05); }
.litis-setup .diff-card-title { font-size: 14px; font-weight: 500; color: var(--dark); margin-bottom: 2px; }
.litis-setup .diff-card.selected .diff-card-title { color: var(--green); }
.litis-setup .diff-card-sub { font-size: 12px; color: var(--muted); }

/* Case box */
.litis-setup .case-box { background: var(--white); border: 1px solid var(--border); border-radius: var(--r); padding: 24px; }
.litis-setup .case-box-empty { text-align: center; padding: 8px 0; }
.litis-setup .case-box-empty p { font-size: 14px; color: var(--muted); margin-bottom: 18px; }
.litis-setup .case-box-loading { display: flex; align-items: center; gap: 12px; color: var(--muted); font-size: 14px; }
.litis-setup .spinner { width: 18px; height: 18px; border: 2px solid var(--bg3); border-top-color: var(--green); border-radius: 50%; animation: litis-spin 0.8s linear infinite; flex-shrink: 0; }
.litis-setup .spinner-sm { width: 15px; height: 15px; border: 2px solid var(--bg3); border-top-color: var(--green); border-radius: 50%; animation: litis-spin 0.8s linear infinite; flex-shrink: 0; }
@keyframes litis-spin { to { transform: rotate(360deg); } }
.litis-setup .case-title { font-size: 17px; font-weight: 500; color: var(--dark); margin-bottom: 6px; }
.litis-setup .case-meta { font-size: 12px; color: var(--muted); margin-bottom: 14px; }
.litis-setup .case-body { font-size: 14px; color: var(--text); line-height: 1.65; margin-bottom: 16px; }
.litis-setup .theory-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.litis-setup .theory-pill { border-radius: 8px; padding: 12px 14px; border: 1px solid var(--border); }
.litis-setup .theory-pill-label { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 6px; }
.litis-setup .theory-pill-label.fis { color: var(--blue); }
.litis-setup .theory-pill-label.def { color: var(--green); }
.litis-setup .theory-pill-text { font-size: 12px; color: var(--muted); line-height: 1.55; }

.litis-setup .btn-generate {
  font-family: var(--sans); font-size: 15px; font-weight: 500;
  background: var(--dark); color: white; border: none;
  padding: 13px 30px; border-radius: var(--r); cursor: pointer; transition: all 0.2s;
}
.litis-setup .btn-generate:hover { background: #2C2820; transform: translateY(-1px); }
.litis-setup .btn-regen {
  font-family: var(--sans); font-size: 12px; font-weight: 500;
  background: var(--bg2); color: var(--muted); border: 1px solid var(--border);
  padding: 7px 14px; border-radius: 8px; cursor: pointer; white-space: nowrap; flex-shrink: 0; transition: all 0.2s;
}
.litis-setup .btn-regen:hover { border-color: rgba(30,26,20,0.2); color: var(--text); }

.litis-setup .case-error { padding: 16px 18px; background: rgba(196,87,42,0.06); border: 1px solid rgba(196,87,42,0.2); border-radius: var(--r); }
.litis-setup .case-error p { font-size: 14px; color: var(--accent2); font-weight: 500; margin-bottom: 10px; }
.litis-setup .btn-retry { font-family: var(--sans); font-size: 13px; font-weight: 500; background: var(--accent); color: white; border: none; padding: 8px 18px; border-radius: 8px; cursor: pointer; }

/* Expediente docs */
.litis-setup .doc-grid { display: flex; flex-direction: column; gap: 8px; }
.litis-setup .doc-card {
  border-radius: var(--r); padding: 14px 16px; display: flex; align-items: center; gap: 12px;
  text-align: left; cursor: pointer; border: 1px solid var(--border); background: var(--white);
  font-family: var(--sans); transition: border-color 0.2s;
}
.litis-setup .doc-card:hover { border-color: rgba(30,26,20,0.2); }
.litis-setup .doc-card-icon { width: 40px; height: 40px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.litis-setup .di-green { background: rgba(112,163,60,0.12); }
.litis-setup .di-blue { background: rgba(74,127,181,0.12); }
.litis-setup .di-accent { background: rgba(196,87,42,0.1); }
.litis-setup .doc-card-name { font-size: 14px; font-weight: 500; color: var(--dark); }
.litis-setup .doc-card-sub { font-size: 12px; color: var(--muted); }
.litis-setup .doc-card-fav { font-size: 9px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; margin-top: 3px; }
.litis-setup .doc-card-open { margin-left: auto; font-size: 11px; font-weight: 600; color: var(--light); flex-shrink: 0; }
.litis-setup .exp-loading { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: var(--white); border: 1px solid var(--border); border-radius: var(--r); }
.litis-setup .exp-loading span { font-size: 13px; color: var(--muted); }
.litis-setup .exp-empty { padding: 12px 16px; background: var(--white); border: 1px solid var(--border); border-radius: var(--r); }
.litis-setup .exp-empty span { font-size: 13px; color: var(--light); }

/* Witness order */
.litis-setup .witnesses-order { display: flex; flex-direction: column; gap: 8px; }
.litis-setup .order-hint { font-size: 13px; color: var(--muted); margin-bottom: 12px; }
.litis-setup .witness-order-item {
  background: var(--white); border: 1px solid var(--border); border-radius: var(--r);
  padding: 14px 16px; display: flex; align-items: center; gap: 12px; cursor: pointer;
  transition: border-color 0.2s; text-align: left; font-family: var(--sans); width: 100%;
}
.litis-setup .witness-order-item:hover { border-color: rgba(30,26,20,0.2); }
.litis-setup .witness-order-item.ordered { border-color: var(--green); }
.litis-setup .witness-order-num {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  background: var(--bg2); display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: var(--muted);
}
.litis-setup .witness-order-item.ordered .witness-order-num { background: var(--green); color: white; }
.litis-setup .witness-order-name { font-size: 14px; font-weight: 500; color: var(--dark); }
.litis-setup .witness-order-role { font-size: 12px; color: var(--muted); }
.litis-setup .witness-note { margin-top: 10px; padding: 10px 14px; border-radius: 8px; font-size: 13px; }
.litis-setup .witness-note.blue { background: rgba(74,127,181,0.07); border: 1px solid rgba(74,127,181,0.15); color: var(--blue); }
.litis-setup .witness-note.accent { background: rgba(196,87,42,0.06); border: 1px solid rgba(196,87,42,0.15); color: var(--accent2); }
.litis-setup .witness-note-label { font-size: 9px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 5px; opacity: 0.8; }
.litis-setup .witness-note-row { font-size: 13px; font-weight: 500; margin-bottom: 2px; }

/* CTA */
.litis-setup .setup-cta { margin-top: 40px; padding-top: 30px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; }
.litis-setup .btn-enter {
  font-family: var(--sans); font-size: 15px; font-weight: 500;
  background: var(--dark); color: white; border: none; padding: 14px 32px;
  border-radius: var(--r); cursor: pointer; transition: opacity 0.2s, transform 0.15s;
}
.litis-setup .btn-enter:hover:not(:disabled) { opacity: 0.85; transform: translateY(-1px); }
.litis-setup .btn-enter:disabled { background: var(--bg3); color: var(--light); cursor: not-allowed; }

@media (max-width: 760px) {
  /* NAV: el título "/ Nueva Simulación" se oculta para liberar espacio */
  .litis-setup .setup-nav { padding: 0 14px; gap: 10px; }
  .litis-setup .setup-nav-title { display: none; }
  .litis-setup .nav-user-name { max-width: 100px; font-size: 12px; }
  .litis-setup .nav-user-img { width: 24px; height: 24px; }
  .litis-setup .btn-sm { padding: 6px 12px; font-size: 12px; }
}

@media (max-width: 640px) {
  /* Grids a 1 columna */
  .litis-setup .crime-grid, .litis-setup .role-grid, .litis-setup .theory-row { grid-template-columns: 1fr; }
  .litis-setup .diff-grid { grid-template-columns: 1fr; }
  .litis-setup .setup-body { padding: 32px 18px 100px; } /* padding-bottom grande para que el CTA no se solape */

  /* Header proporcional */
  .litis-setup .setup-header h1 { font-size: 26px; }
  .litis-setup .setup-header p { font-size: 14px; }

  /* Cards más compactas */
  .litis-setup .crime-card, .litis-setup .role-card { padding: 16px 18px; }
  .litis-setup .crime-card-title, .litis-setup .role-card-title { font-size: 15px; }
  .litis-setup .case-box { padding: 18px; }
  .litis-setup .case-title { font-size: 15px; }
  .litis-setup .case-body { font-size: 13px; }

  /* CTA: full-width, fijo abajo con safe-area-inset (notch / barra de Chrome) */
  .litis-setup .setup-cta {
    position: sticky; bottom: 0;
    margin-top: 30px; padding: 16px 0 calc(16px + env(safe-area-inset-bottom));
    background: linear-gradient(to top, var(--bg) 60%, rgba(236,234,227,0));
    border-top: none;
    justify-content: stretch;
  }
  .litis-setup .btn-enter { width: 100%; padding: 15px 22px; font-size: 14px; }

  /* Documento cards: que no se desborden */
  .litis-setup .doc-card { padding: 12px 14px; }
  .litis-setup .doc-card-icon { width: 36px; height: 36px; font-size: 16px; }
  .litis-setup .doc-card-name { font-size: 13px; }
  .litis-setup .doc-card-sub { font-size: 11px; }
  .litis-setup .doc-card-open { font-size: 10px; }

  /* Witness order */
  .litis-setup .witness-order-item { padding: 12px 14px; }
  .litis-setup .witness-order-name { font-size: 13px; }
  .litis-setup .witness-order-role { font-size: 11px; }
}

@media (max-width: 380px) {
  .litis-setup .setup-header h1 { font-size: 22px; }
  .litis-setup .nav-user-name { display: none; }
}
`;

export default function Setup({ user, onStart, onSignOut }) {
  const [crimeKey,    setCrimeKey]    = useState("lesiones");
  const [userRole,    setUserRole]    = useState("fiscal");
  const [userName,    setUserName]    = useState(user?.displayName || "");
  const [difficulty,  setDifficulty]  = useState("intermedio");
  const [caseData,    setCaseData]    = useState(null);
  const [generating,  setGenerating]  = useState(false);
  const [genError,    setGenError]    = useState(null);
  const [expediente,  setExpediente]  = useState(null);
  const [expLoading,  setExpLoading]  = useState(false);
  const [fiscalOrder, setFiscalOrder] = useState([]);

  // ── Handlers (lógica intacta) ───────────────────────────────────────────
  const reset = () => { setCaseData(null); setFiscalOrder([]); setExpediente(null); setGenError(null); };
  const handleCrime = k => { setCrimeKey(k);   reset(); };
  const handleRole  = r => { setUserRole(r);   reset(); };
  const handleDiff  = d => { setDifficulty(d); reset(); };

  const generateCase = async () => {
    setGenerating(true); setGenError(null); setCaseData(null); setFiscalOrder([]); setExpediente(null);
    try {
      const raw    = await callClaude([{ role:"user", content:"Genera el caso ahora." }], buildCasePrompt(crimeKey, userRole, difficulty), 6000, true);
      const cleaned = raw.replace(/```json|```/g, "").trim();
      const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("Respuesta sin JSON válido");
      const parsed = JSON.parse(jsonMatch[0]);
      setCaseData(parsed);
      generateExpediente(parsed);
    } catch (e) {
      console.error("Error generando caso:", e);
      setGenError(`Error: ${e.message || "Intenta de nuevo."}`);
    }
    setGenerating(false);
  };

  const generateExpediente = async (cd) => {
    setExpLoading(true);
    const witnesses = cd.testigos || [];
    const perito    = witnesses.find(w => w.tipo === "perito" || w.tipo === "experto");
    const policia   = witnesses.find(w => w.tipo === "policia");
    try {
      const tasks = [];
      const callDoc = (messages, system) => callClaude(messages, system, 800, false, true);

      if (policia) tasks.push(
        callDoc([{ role:"user", content:"Genera el parte policial." }], buildReportPrompt(policia, cd, crimeKey))
          .then(c => ({ key:"policia", witness:policia, content:c }))
      );
      if (perito) tasks.push(
        callDoc([{ role:"user", content:"Genera el informe pericial." }], buildReportPrompt(perito, cd, crimeKey))
          .then(c => ({ key:"perito", witness:perito, content:c }))
      );
      tasks.push(
        callDoc([{ role:"user", content:"Genera el documento probatorio." }], buildDocumentaryEvidencePrompt(cd, crimeKey))
          .then(c => ({ key:"prueba", content:c }))
      );
      const results = await Promise.all(tasks);
      const exp = {};
      results.forEach(r => { exp[r.key] = r; });
      setExpediente(exp);
    } catch (e) {
      console.error("generateExpediente error:", e);
    }
    setExpLoading(false);
  };

  // ── Witness queue builder (lógica intacta) ────────────────────────────────
  const fiscalWitnesses  = caseData?.testigos?.filter(w => w.propietario === "fiscal") || [];
  const defenseWitnesses = caseData?.testigos?.filter(w => w.propietario === "defensa") || [];

  const toggleFiscalOrder = idx => {
    setFiscalOrder(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  const buildQueue = () => {
    const orderedFiscal = (userRole === "fiscal" && fiscalOrder.length === fiscalWitnesses.length)
      ? fiscalOrder.map(i => fiscalWitnesses[i])
      : fiscalWitnesses;
    return [
      ...orderedFiscal.map(w  => ({ ...w, startRole: userRole === "fiscal"   ? "user" : "ai" })),
      ...defenseWitnesses.map(w => ({ ...w, startRole: userRole === "defensor" ? "user" : "ai" })),
    ];
  };

  const allFiscalOrdered = userRole === "defensor" || fiscalOrder.length === fiscalWitnesses.length;
  const canStart = caseData && !generating && userName.trim().length >= 2 && allFiscalOrdered;

  const diffOptions = [
    { k:"basico", label:"Básico", desc:"Caso favorable a ti" },
    { k:"intermedio", label:"Intermedio", desc:"Caso equilibrado" },
    { k:"avanzado", label:"Avanzado", desc:"Adversario agresivo" },
  ];
  const roleOptions = [
    { k:"fiscal", label:"Fiscal del Estado", desc:"Presentas tus testigos de cargo primero" },
    { k:"defensor", label:"Defensor Penal", desc:"La Fiscalía (IA) presenta sus testigos primero" },
  ];

  const ctaLabel = generating ? "Generando caso…"
    : !userName.trim() ? "Escribe tu nombre para continuar"
    : userRole === "fiscal" && !allFiscalOrdered ? "Selecciona el orden de tus testigos"
    : "Ingresar a la sala de juicio";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyles />
      <style dangerouslySetInnerHTML={{ __html: SETUP_CSS }} />
      <div className="litis-setup">

        {/* Nav */}
        <nav className="setup-nav">
          <img src="/litislab-logo.png" alt="LitisLab" className="setup-nav-logo" />
          <span className="setup-nav-title">/ Nueva Simulación</span>
          <div className="setup-nav-right">
            <div className="nav-user">
              {user?.photoURL && <img src={user.photoURL} alt="" className="nav-user-img" referrerPolicy="no-referrer" />}
              <span className="nav-user-name">{user?.displayName || user?.email}</span>
            </div>
            <button className="btn-sm" onClick={onSignOut}>Salir</button>
          </div>
        </nav>

        <div className="setup-body">
          <div className="setup-header">
            <h1>Nueva Simulación</h1>
            <p>Configura tu práctica de juicio oral bajo el COIP</p>
          </div>

          {/* 01 Delito */}
          <div className="setup-step">
            <div className="step-label"><span className="step-label-num done">1</span>Tipo de delito</div>
            <div className="crime-grid">
              {Object.values(CRIMES).map(c => (
                <button key={c.key} className={`crime-card ${crimeKey === c.key ? "selected" : ""}`} onClick={() => handleCrime(c.key)}>
                  <div className="crime-card-coip">{c.article}</div>
                  <div className="crime-card-title">{c.label}</div>
                  <div className="crime-card-desc">{c.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* 02 Rol */}
          <div className="setup-step">
            <div className="step-label"><span className="step-label-num done">2</span>Tu rol en sala</div>
            <div className="role-grid">
              {roleOptions.map(({ k, label, desc }) => (
                <button key={k} className={`role-card ${userRole === k ? "selected" : ""}`} onClick={() => handleRole(k)}>
                  <div className="role-card-title">{label}</div>
                  <div className="role-card-desc">{desc}</div>
                </button>
              ))}
            </div>
            <div className="name-label">Tu nombre — la Secretaría te presentará así en sala</div>
            <input
              type="text" className="name-input" value={userName}
              onChange={e => setUserName(e.target.value)}
              placeholder={userRole === "fiscal" ? "Ej: Dra. Ana Martínez Vega" : "Ej: Dr. Carlos Suárez Pinto"}
            />
          </div>

          {/* 03 Dificultad */}
          <div className="setup-step">
            <div className="step-label"><span className="step-label-num done">3</span>Dificultad</div>
            <div className="diff-grid">
              {diffOptions.map(({ k, label, desc }) => (
                <button key={k} className={`diff-card ${difficulty === k ? "selected" : ""}`} onClick={() => handleDiff(k)}>
                  <div className="diff-card-title">{label}</div>
                  <div className="diff-card-sub">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* 04 Caso */}
          <div className="setup-step">
            <div className="step-label"><span className={`step-label-num ${caseData ? "done" : ""}`}>4</span>Expediente generado por IA</div>
            <div className="case-box">
              {!caseData && !generating && !genError && (
                <div className="case-box-empty">
                  <p>Selecciona tu rol y la dificultad primero, luego genera el expediente.</p>
                  <button className="btn-generate" onClick={generateCase}>Generar caso con IA ⚖</button>
                </div>
              )}
              {generating && (
                <div className="case-box-loading">
                  <div className="spinner" />
                  <span>Generando expediente de {CRIMES[crimeKey]?.label}… <span style={{ color: "var(--light)" }}>15-20 segundos</span></span>
                </div>
              )}
              {genError && (
                <div className="case-error">
                  <p>{genError}</p>
                  <button className="btn-retry" onClick={generateCase}>Reintentar</button>
                </div>
              )}
              {caseData && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 6 }}>
                    <div className="case-title">{caseData.titulo}</div>
                    <button className="btn-regen" onClick={generateCase}>↻ Otro caso</button>
                  </div>
                  <div className="case-meta">Procesado: {caseData.procesado} &nbsp;·&nbsp; Víctima: {caseData.victima}</div>
                  <div className="case-body">{caseData.resumen}</div>
                  <div className="theory-row">
                    <div className="theory-pill">
                      <div className="theory-pill-label fis">Teoría Fiscalía</div>
                      <div className="theory-pill-text">{caseData.teoria_fiscal}</div>
                    </div>
                    <div className="theory-pill">
                      <div className="theory-pill-label def">Teoría Defensa</div>
                      <div className="theory-pill-text">{caseData.teoria_defensa}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 05 Expediente */}
          {caseData && (
            <div className="setup-step">
              <div className="step-label"><span className={`step-label-num ${expediente ? "done" : ""}`}>5</span>Expediente — documentos del caso</div>
              {expLoading && (
                <div className="exp-loading">
                  <div className="spinner-sm" />
                  <span>Generando documentos del expediente en paralelo…</span>
                </div>
              )}
              {expediente && (
                <div className="doc-grid">
                  {expediente.policia && (
                    <button className="doc-card" onClick={() => openDoc(generateReportHTML(expediente.policia.witness, expediente.policia.content, caseData))}>
                      <div className="doc-card-icon di-green">📋</div>
                      <div>
                        <div className="doc-card-name">Parte Policial</div>
                        <div className="doc-card-sub">{expediente.policia.witness.nombre} · Leer antes de entrar a sala</div>
                      </div>
                      <span className="doc-card-open">ABRIR →</span>
                    </button>
                  )}
                  {expediente.perito && (
                    <button className="doc-card" onClick={() => openDoc(generateReportHTML(expediente.perito.witness, expediente.perito.content, caseData))}>
                      <div className="doc-card-icon di-blue">🔬</div>
                      <div>
                        <div className="doc-card-name">Informe Pericial</div>
                        <div className="doc-card-sub">{expediente.perito.witness.nombre} · Leer antes de entrar a sala</div>
                      </div>
                      <span className="doc-card-open">ABRIR →</span>
                    </button>
                  )}
                  {expediente.prueba && caseData.prueba_documental && (
                    <button className="doc-card" onClick={() => openDoc(generateDocHTML(caseData.prueba_documental.tipo, caseData.prueba_documental.tipo, expediente.prueba.content, caseData))}>
                      <div className="doc-card-icon di-accent">📑</div>
                      <div style={{ flex: 1 }}>
                        <div className="doc-card-name">{caseData.prueba_documental.tipo}</div>
                        <div className="doc-card-sub">{caseData.prueba_documental.descripcion}</div>
                        <div className="doc-card-fav" style={{ color: caseData.prueba_documental.favorable_a === "fiscal" ? "var(--blue)" : "var(--green)" }}>
                          Favorable a: {caseData.prueba_documental.favorable_a === "fiscal" ? "Fiscalía" : "Defensa"}
                        </div>
                      </div>
                      <span className="doc-card-open">ABRIR →</span>
                    </button>
                  )}
                </div>
              )}
              {!expLoading && !expediente && (
                <div className="exp-empty">
                  <span>Los documentos se generarán automáticamente al crear el caso.</span>
                </div>
              )}
            </div>
          )}

          {/* 06 Orden testigos — Fiscal */}
          {caseData && userRole === "fiscal" && (
            <div className="setup-step">
              <div className="step-label"><span className={`step-label-num ${allFiscalOrdered ? "done" : ""}`}>6</span>Orden de presentación de tus testigos</div>
              <div className="order-hint">Haz clic en el orden en que quieres llamarlos a estrado — primero, segundo, tercero.</div>
              <div className="witnesses-order">
                {fiscalWitnesses.map((w, i) => {
                  const pos = fiscalOrder.indexOf(i);
                  const selected = pos >= 0;
                  return (
                    <button key={i} className={`witness-order-item ${selected ? "ordered" : ""}`} onClick={() => toggleFiscalOrder(i)}>
                      <div className="witness-order-num">{selected ? pos + 1 : "?"}</div>
                      <div>
                        <div className="witness-order-name">{w.nombre}</div>
                        <div className="witness-order-role">{WITNESS_TYPES[w.tipo]?.label || "Testigo"}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {defenseWitnesses.length > 0 && (
                <div className="witness-note blue">
                  <div className="witness-note-label">Testigo de la Defensa (al final, automático)</div>
                  {defenseWitnesses.map((w, i) => (
                    <div key={i} className="witness-note-row">{w.nombre} — {WITNESS_TYPES[w.tipo]?.label || "Testigo"}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 06 Vista testigos — Defensor */}
          {caseData && userRole === "defensor" && (
            <div className="setup-step">
              <div className="step-label"><span className="step-label-num done">6</span>Testigos del juicio</div>
              <div className="witness-note accent" style={{ marginBottom: 8 }}>
                <div className="witness-note-label">Testigos de la Fiscalía (la IA interroga primero)</div>
                {fiscalWitnesses.map((w, i) => (
                  <div key={i} className="witness-note-row">{w.nombre} — {WITNESS_TYPES[w.tipo]?.label || "Testigo"}</div>
                ))}
              </div>
              <div className="witness-note blue">
                <div className="witness-note-label">Tu testigo (tú interrogas)</div>
                {defenseWitnesses.map((w, i) => (
                  <div key={i} className="witness-note-row">{w.nombre} — {WITNESS_TYPES[w.tipo]?.label || "Testigo"}</div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="setup-cta">
            <button
              className="btn-enter"
              disabled={!canStart}
              onClick={() => { if (!canStart) return; onStart({ crimeKey, userRole, userName: userName.trim(), difficulty, caseData, expediente, witnessQueue: buildQueue() }); }}
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
